import React from 'react';
import { makeStyles, Grid, Typography, Button, CircularProgress } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { ROUTE_USAGE_DETAIL } from 'app/routes';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import dayjs from 'dayjs';
import StatCard from './StatCard';
import ApplicationSection from './ApplicationSection';
import { useQuery } from 'hooks/useQuery';
import { getUsageAnalyticsDashboard } from 'modules/api/usageAnalytics';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: 0,
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(3),
        color: '#292929',
    },
    title: {
        fontSize: 28,
        fontWeight: 700,
        color: '#292929',
        lineHeight: 1.2,
    },
    subtitle: {
        fontSize: 16,
        fontWeight: 500,
        textTransform: 'capitalize',
        color: '#0B3F30',
        marginTop: 2,
    },
    detailBtn: {
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.46px',
        border: '1px solid #ccc',
        color: '#2B2B2B',
        textTransform: 'uppercase',
        padding: '6px 16px',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            border: '1px solid #bbb',
        },
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    productRow: {
        marginBottom: theme.spacing(3),
    },
    bottomBtn: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(1),
    },
    loader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(6),
    },
}));

function computeChange(current, prev) {
    if (!prev) return null;
    return Math.round(((current - prev) / prev) * 100);
}

function computeAbsoluteChange(current, prev) {
    if (current == null || prev == null) return null;
    return current - prev;
}

export default function DashboardOverview() {
    const classes = useStyles();
    const history = useHistory();

    const { data, isLoading, error } = useQuery([getUsageAnalyticsDashboard]);

    if (isLoading) {
        return (
            <div className={classes.loader}>
                <CircularProgress color="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.loader}>
                <Typography style={{ color: '#c62828', fontSize: 14 }}>
                    Nepavyko įkelti duomenų. Bandykite dar kartą.
                </Typography>
            </div>
        );
    }

    const curr = data?.current ?? {};
    const prev = data?.previous ?? {};

    const currentTo = data?.currentPeriodTo
        ? dayjs(data.currentPeriodTo)
        : (data?.previousPeriodTo ? dayjs(data.previousPeriodTo).add(1, 'month') : dayjs());
    const currentFrom = data?.currentPeriodFrom
        ? dayjs(data.currentPeriodFrom)
        : currentTo.startOf('month');
    const currentPeriodLabel = currentTo.format(`YYYY MMMM [${currentFrom.date()}-]D`);

    const subtext = data?.previousPeriodTo
        ? (() => {
              const to = dayjs(data.previousPeriodTo);
              const from = data.previousPeriodFrom ? dayjs(data.previousPeriodFrom) : to.startOf('month');
              const monthRange = to.format(`MMMM [${from.date()}-]D`);
              const capitalized = monthRange.charAt(0).toUpperCase() + monthRange.slice(1);
              return `Lyginant su ${capitalized}`;
          })()
        : 'Nei praėjusį mėn.';

    const mscanCards = [
        { label: 'Klientai', value: curr.mscanClients ?? null, change: computeAbsoluteChange(curr.mscanClients, prev.mscanClients), absolute: true },
        { label: 'Vartotojai', value: curr.mscanUsers ?? null, change: computeAbsoluteChange(curr.mscanUsers, prev.mscanUsers), absolute: true },
        { label: 'Lokacijos klientai', value: curr.mscanLocationClients ?? null, change: computeAbsoluteChange(curr.mscanLocationClients, prev.mscanLocationClients), absolute: true },
        { label: 'Lokacijos operacijos', value: curr.mscanLocationOperations ?? null, change: computeChange(curr.mscanLocationOperations, prev.mscanLocationOperations) },
    ];

    const mgamaCards = [
        { label: 'Klientai', value: curr.mgamaClients ?? null, change: computeAbsoluteChange(curr.mgamaClients, prev.mgamaClients), absolute: true },
        { label: 'Vartotojai', value: curr.mgamaUsers ?? null, change: computeAbsoluteChange(curr.mgamaUsers, prev.mgamaUsers), absolute: true },
    ];

    return (
        <div className={classes.root}>
            {/* Page header */}
            <div className={classes.pageHeader}>
                <div>
                    <Typography className={classes.title}>Apžvalga</Typography>
                    <Typography className={classes.subtitle}>{currentPeriodLabel}</Typography>
                </div>
                <Button
                    className={classes.detailBtn}
                    variant="outlined"
                    endIcon={<ChevronRightIcon />}
                    disableElevation
                    onClick={() => history.push(ROUTE_USAGE_DETAIL.path)}
                >
                    Detali informacija
                </Button>
            </div>

            {/* Top stat cards */}
            <Grid container spacing={2} className={classes.section}>
                <Grid item xs>
                    <StatCard
                        label="Duomenų bazės"
                        value={curr.databases ?? null}
                        change={computeAbsoluteChange(curr.databases, prev.databases)}
                        absolute
                        subtext={subtext}
                    />
                </Grid>
                <Grid item xs>
                    <StatCard
                        label="API raktai (aktyvūs)"
                        value={curr.apiKeys ?? null}
                        change={computeAbsoluteChange(curr.apiKeys, prev.apiKeys)}
                        absolute
                        subtext={subtext}
                    />
                </Grid>
                <Grid item xs>
                    <StatCard
                        label="API užklausos"
                        value={curr.apiQueries ?? null}
                        change={computeChange(curr.apiQueries, prev.apiQueries)}
                        subtext={subtext}
                    />
                </Grid>
                <Grid item xs>
                    <StatCard
                        label="OPB užklausos"
                        value={curr.opbQueries ?? null}
                        change={computeChange(curr.opbQueries, prev.opbQueries)}
                        subtext={subtext}
                    />
                </Grid>
                <Grid item xs>
                    <StatCard
                        label="SP užklausos"
                        value={curr.spQueries ?? null}
                        change={computeChange(curr.spQueries, prev.spQueries)}
                        subtext={subtext}
                    />
                </Grid>
            </Grid>

            {/* MSCAN + MGAMA sections */}
            <Grid container spacing={2} className={classes.productRow}>
                <Grid item xs={6}>
                    <ApplicationSection title="MSCAN" cards={mscanCards} subtext={subtext} />
                </Grid>
                <Grid item xs={6}>
                    <ApplicationSection title="MGAMA" cards={mgamaCards} subtext={subtext} />
                </Grid>
            </Grid>
        </div>
    );
}
